import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_ACTIVE_ALARMS_API,
	GET_HISTORICAL_ALARMS_API,
	GET_SWITCH_ALARMS_API,
} from '../../../constants/commonConstants';
import {encryptData} from '../../../utils/crypto';
import {
	decodeAgentResponse,
	LOOKUP_FALLBACK_LIMIT,
	LOOKUP_LIMIT,
	optionalText,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';
import {
	normalizeSearchFields,
	resolveByFields,
} from '../../core/fieldSearch';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return an alarm row identifier.
 * @param {object} item Canvas alarm row
 * @return {*} identifier
 */
function alarmId(item) {
	return item?.alarm_id ??
		item?.id ??
		item?.event_id ??
		item?.event_name ??
		item?.alarm_description;
}

/**
 * Display name for an alarm row.
 * @param {object} item Canvas alarm row
 * @param {string} entityType agent entity type
 * @return {string} name
 */
function alarmName(item, entityType) {
	const eventName = optionalText(item?.event_name) ||
		optionalText(item?.description) ||
		optionalText(item?.alarm_description);
	if (eventName) return eventName;
	if (entityType === 'switch_event') {
		return optionalText(item?.device_name) ||
			optionalText(item?.switch_name) ||
			(alarmId(item) == null ? '' : String(alarmId(item)));
	}
	return optionalText(item?.ap_name) ||
		optionalText(item?.device_name) ||
		(alarmId(item) == null ? '' : String(alarmId(item)));
}

/**
 * Whether a Canvas row is a real alarm/event, not an empty placeholder.
 * @param {object} item Canvas row
 * @return {boolean} true when the row has event data
 */
function hasEventData(item) {
	return Boolean(
		optionalText(item?.event_name) ||
		optionalText(item?.description) ||
		optionalText(item?.alarm_description) ||
		optionalText(item?.device_name) ||
		optionalText(item?.switch_name),
	);
}

/**
 * Read the raw page from every alarm-list envelope the APIs use.
 * @param {*} document decoded API response
 * @return {Array} unfiltered rows
 */
function envelopeRows(document) {
	if (Array.isArray(document)) return document;
	const nested = document?.results?.results;
	if (Array.isArray(nested)) return nested;
	const dataRows = document?.data?.results;
	if (Array.isArray(dataRows)) return dataRows;
	const page = responseRows(document);
	return Array.isArray(page) ? page : [];
}

/**
 * Normalize a Canvas alarm row for a resolver entity type.
 * @param {string} entityType agent entity type
 * @param {object} item Canvas row
 * @return {object|null} agent candidate
 */
function normalizeAlarm(entityType, item) {
	const id = alarmId(item);
	const name = alarmName(item, entityType);
	if (id == null || !name) return null;
	return {
		entity_type: entityType,
		entity_id: String(id),
		name: String(name),
		site_id: item?.site_id == null ? null : String(item.site_id),
		site_name: optionalText(item?.site_name),
		serial_no: optionalText(item?.device_id),
	};
}

/**
 * Map a stored field value to its catalogue label.
 * @param {object} field catalogue field
 * @param {*} stored stored field value
 * @return {string|null} label
 */
function labelFromChoices(field, stored) {
	if (stored == null || stored === '') return null;
	const text = String(stored).trim();
	const folded = text.toLocaleLowerCase();
	for (const choice of field.choices || []) {
		const names = [
			choice.id,
			choice.label,
			...(choice.values || []),
			...(choice.aliases || []),
		];
		if (names.some((name) => String(name).toLocaleLowerCase() === folded)) {
			return choice.label;
		}
	}
	return text;
}

/**
 * Copy a Canvas row with catalogue labels the lookup can read.
 * @param {object} item Canvas row
 * @param {*} searchFields field catalogue sent with the lookup
 * @return {object} row
 */
function rowForSearch(item, searchFields) {
	const next = {
		...item,
		event_name: optionalText(item?.event_name)
			|| optionalText(item?.description)
			|| optionalText(item?.alarm_description)
			|| '',
		ap_name: optionalText(item?.ap_name) || optionalText(item?.device_name) || '',
		site_name: optionalText(item?.site_name) || '',
		ap_ipv4: optionalText(item?.ap_ipv4) || '',
		ssid_name: optionalText(item?.ssid_name) || '',
		device_type: optionalText(item?.device_type) || '',
		severity_level: optionalText(item?.severity_level)
			|| optionalText(item?.severity_type)
			|| '',
	};
	normalizeSearchFields(searchFields).forEach((field) => {
		for (const attribute of field.attributes) {
			if (!Object.prototype.hasOwnProperty.call(next, attribute)) continue;
			const labelled = labelFromChoices(field, next[attribute]);
			if (labelled != null) {
				next[attribute] = labelled;
				return;
			}
		}
	});
	return next;
}

/**
 * POST an encrypted alarm list page.
 * @param {string} url list URL
 * @param {object} filters request body
 * @return {Promise<object>} decoded document
 */
async function postList(url, filters) {
	const {data} = await axios.request({
		method: 'post',
		maxBodyLength: Infinity,
		url,
		data: encryptData(JSON.stringify(filters || {})),
	});
	return decodeAgentResponse(data) || {};
}

/**
 * Load alarm rows, paging until the lookup cap.
 * @param {string} listPath API path
 * @param {string|null} search name search
 * @param {number} limit maximum rows
 * @return {Promise<Array>} rows
 */
async function loadRows(listPath, search, limit) {
	const rows = [];
	const pageSize = Math.min(Math.max(limit, 1), LOOKUP_LIMIT);
	let offset = 0;
	while (rows.length < limit) {
		const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
		const url = `${ENDPOINT_URL}${listPath}?limit=${pageSize}&offset=${offset}${searchParam}`;
		const document = await postList(url, {});
		const raw = envelopeRows(document);
		rows.push(...raw.filter(hasEventData));
		// Page on the raw envelope so empty placeholders do not stop the
		// walk before the rows that actually have event data.
		if (raw.length < pageSize) break;
		offset += pageSize;
	}
	return rows.slice(0, limit);
}

/**
 * Rank loaded rows through the catalogue the agent sent.
 * @param {string} entityType agent entity type
 * @param {Array} rows Canvas rows
 * @param {object} params resolver parameters
 * @param {string} query lookup query
 * @param {number} limit maximum candidates
 * @param {boolean} [fuzzy] allow near-miss matches
 * @return {Array} ranked candidates
 */
function candidatesFromRows(entityType, rows, params, query, limit, fuzzy = false) {
	return resolveByFields({
		rows: rows.map((row) => rowForSearch(row, params.search_fields)),
		normalizeRow: (item) => normalizeAlarm(entityType, item),
		query,
		limit,
		searchFields: params.search_fields,
		rank: rankLookupCandidates,
		fallbackValues: (candidate) => [candidate?.name],
		fuzzy,
	});
}

/**
 * Resolve alarm candidates for one Maintenance list.
 * @param {string} listPath API path
 * @param {string} entityType agent entity type
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
async function resolveAlarmList(listPath, entityType, params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(listPath, null, limit);
		replaceResolvedEntities(entityType, rows, alarmId);
		return {
			candidates: candidatesFromRows(
				entityType, rows, params, '', limit,
			).slice(0, limit),
		};
	}
	let rows = await loadRows(listPath, query, LOOKUP_LIMIT);
	let candidates = candidatesFromRows(
		entityType, rows, params, query, LOOKUP_LIMIT,
	);
	// The list API already applied `search`. If the catalogue then drops
	// every row, keep the API hits instead of pretending nothing matched
	// and falling through to Sites.
	if (!candidates.length && rows.length) {
		candidates = candidatesFromRows(
			entityType, rows, params, '', LOOKUP_LIMIT,
		);
	}
	if (!candidates.length) {
		rows = await loadRows(listPath, null, LOOKUP_FALLBACK_LIMIT);
		candidates = candidatesFromRows(
			entityType, rows, params, query, LOOKUP_LIMIT,
		);
		if (!candidates.length && params.fuzzy === true) {
			candidates = candidatesFromRows(
				entityType, rows, params, query, LOOKUP_LIMIT, true,
			);
		}
	}
	replaceResolvedEntities(entityType, rows, alarmId);
	return {candidates};
}

/**
 * Resolve Active Alarm candidates, paging past the first table page.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export function resolveActiveAlarms(params) {
	return resolveAlarmList(GET_ACTIVE_ALARMS_API, 'alarm', params);
}

/**
 * Resolve Historical Alarm candidates, paging past the first table page.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export function resolveHistoricalAlarms(params) {
	return resolveAlarmList(GET_HISTORICAL_ALARMS_API, 'historical_alarm', params);
}

/**
 * Resolve Switch Event candidates, paging past the first table page.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export function resolveSwitchEvents(params) {
	return resolveAlarmList(`${GET_SWITCH_ALARMS_API}0/`, 'switch_event', params);
}

/**
 * Return a retained alarm row.
 * @param {string} entityType agent entity type
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedAlarm(entityType, entityId) {
	return getResolvedEntity(entityType, entityId);
}
